class MainCarousel extends HTMLElement {
    constructor() {
        super()

        this.carousel = null
        this.autoplayInterval = null
        // Lê velocidade do data attribute ou usa padrão de 5 segundos
        const speedSeconds = parseInt(this.getAttribute('data-autoplay-speed') || '5', 10)
        this.autoplaySpeed = speedSeconds * 1000 // Converte para milissegundos
        // Verifica se autoplay está habilitado via data attribute
        this.autoplayEnabled = this.getAttribute('data-autoplay-enabled') !== 'false'
        this.isPaused = false
        this.isHovered = false
        
        this.init()
    }

    init() {
        // Verifica preferência de movimento reduzido
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)')
        
        // Inicializa o carrossel
        this.carousel = new Carousel(this, {
            infinite: true,
            transition: "slide"
        })

        // Aguarda o carrossel estar pronto
        this.carousel.on('ready', () => {
            this.setupAutoplay()
        })

        // Se o carrossel já estiver pronto, configura o autoplay
        if (this.carousel.state === 1) { // Ready state
            this.setupAutoplay()
        }

        // Event listeners para pausar no hover
        this.addEventListener('mouseenter', () => {
            this.isHovered = true
            this.pauseAutoplay()
        })

        this.addEventListener('mouseleave', () => {
            this.isHovered = false
            if (!this.isPaused) {
                this.startAutoplay()
            }
        })

        // Pausar quando houver interação (clique, touch)
        this.addEventListener('click', () => {
            this.pauseAutoplay()
            // Retoma após 10 segundos se não estiver com hover
            setTimeout(() => {
                if (!this.isHovered && !this.isPaused) {
                    this.startAutoplay()
                }
            }, 10000)
        })
    }

    setupAutoplay() {
        // Verifica se autoplay está habilitado
        if (!this.autoplayEnabled) {
            return
        }

        // Verifica se há mais de 1 slide
        if (!this.carousel || this.carousel.pages.length <= 1) {
            return
        }

        // Respeita preferência de movimento reduzido
        if (this.reducedMotion.matches) {
            return
        }

        // Inicia o autoplay
        this.startAutoplay()

        // Monitora mudanças na preferência de movimento
        if (this.reducedMotion.addEventListener) {
            this.reducedMotion.addEventListener('change', () => {
                if (this.reducedMotion.matches) {
                    this.pauseAutoplay()
                } else if (this.autoplayEnabled) {
                    this.startAutoplay()
                }
            })
        }
    }

    startAutoplay() {
        // Limpa intervalo existente
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval)
        }

        // Não inicia se já estiver pausado ou reduzir movimento
        if (this.isPaused || this.reducedMotion.matches) {
            return
        }

        // Verifica se há mais de 1 slide antes de criar o intervalo
        if (!this.carousel || this.carousel.pages.length <= 1) {
            return // Não inicia autoplay se houver apenas 1 slide
        }

        // Cria novo intervalo
        this.autoplayInterval = setInterval(() => {
            // Verifica se ainda há mais de 1 slide antes de avançar
            if (!this.carousel || this.carousel.pages.length <= 1) {
                this.pauseAutoplay() // Para o autoplay se houver apenas 1 slide
                return
            }
            this.carousel.slideNext()
        }, this.autoplaySpeed)
    }

    pauseAutoplay() {
        if (this.autoplayInterval) {
            clearInterval(this.autoplayInterval)
            this.autoplayInterval = null
        }
    }

    // Métodos públicos para controle manual
    play() {
        this.isPaused = false
        this.startAutoplay()
    }

    pause() {
        this.isPaused = true
        this.pauseAutoplay()
    }

    // Limpa recursos ao desconectar
    disconnectedCallback() {
        this.pauseAutoplay()
        if (this.reducedMotion && this.reducedMotion.removeEventListener) {
            this.reducedMotion.removeEventListener('change', this.setupAutoplay)
        }
    }
}

customElements.define('main-carousel', MainCarousel)

class MainBannerCarousel extends HTMLElement {
    constructor() {
        super()

        this.items = []
        this.currentIndex = 0
        this.frameId = null
        this.lastSwitchAt = 0
        this.cycleDelay = parseInt(this.getAttribute('data-cycle-delay') || '3500', 10)
        this.mediaQuery = window.matchMedia('(min-width: 993px)')
        this.handleMediaChange = this.handleMediaChange.bind(this)
        this.tick = this.tick.bind(this)
    }

    connectedCallback() {
        this.items = Array.from(this.querySelectorAll('.content-images__item'))

        if (this.items.length === 0) {
            return
        }

        if (this.mediaQuery.addEventListener) {
            this.mediaQuery.addEventListener('change', this.handleMediaChange)
        } else if (this.mediaQuery.addListener) {
            this.mediaQuery.addListener(this.handleMediaChange)
            this.items.forEach(item => item.classList.remove('is-active'))
        }

        this.syncState()
    }

    disconnectedCallback() {
        this.stop()

        if (this.mediaQuery.removeEventListener) {
            this.mediaQuery.removeEventListener('change', this.handleMediaChange)
        } else if (this.mediaQuery.removeListener) {
            this.mediaQuery.removeListener(this.handleMediaChange)
        }
    }

    handleMediaChange() {
        this.syncState()
    }

    syncState() {
        if (this.items.length <= 1) {
            this.showItem(0)
            return
        }

        if (this.mediaQuery.matches) {
            this.start()
        } else {
            this.stop()
            this.items.forEach((item) => item.classList.remove('is-active'))
        }
    }

    start() {
        if (this.frameId) {
            return
        }

        this.currentIndex = Math.max(0, this.items.findIndex((item) => item.classList.contains('is-active')))
        this.lastSwitchAt = window.performance.now()
        this.showItem(this.currentIndex)
        this.frameId = window.requestAnimationFrame(this.tick)
    }

    stop() {
        if (this.frameId) {
            window.cancelAnimationFrame(this.frameId)
            this.frameId = null
        }
    }

    tick(now) {
        if (!this.mediaQuery.matches || this.items.length <= 1) {
            this.stop()
            this.showItem(0)
            return
        }

        if (now - this.lastSwitchAt >= this.cycleDelay) {
            this.currentIndex = (this.currentIndex + 1) % this.items.length
            this.showItem(this.currentIndex)
            this.lastSwitchAt = now
        }

        this.frameId = window.requestAnimationFrame(this.tick)
    }

    showItem(index) {
        this.items.forEach((item, itemIndex) => {
            item.classList.toggle('is-active', itemIndex === index)
        })
    }
}

customElements.define('main-banner-carousel', MainBannerCarousel)

class CardSlider extends HTMLElement {
    constructor() {
        super()

        this.init()
    }

    init() {
        this.style.setProperty('--slides-per-page', this.getAttribute('data-slidesPerPage'));

        new Carousel(this, {
            infinite: false,
            center: false,
            slidesPerPage: 'auto',
            transition: false,
        })
    }
}

customElements.define('card-slider', CardSlider)

class Filters extends HTMLElement {
    constructor() {
        super()

        this.buttonsClose = this.querySelectorAll('[data-close-filter]')
        this.buttonsOpen = document.querySelectorAll('[data-open-filter]')

        this.listeners()
    }

    listeners() {
        this.buttonsClose.forEach((button) => button.addEventListener("click", this.closeFilter.bind(this)))
        this.buttonsOpen.forEach((button) => button.addEventListener("click", this.openFilters.bind(this)))
    }

    closeFilter() {
        this.removeAttribute("show")
    }

    openFilters() {
        this.setAttribute("show", "")
    }
}

customElements.define("custom-filters", Filters);

class ModalForm extends HTMLElement {
    constructor() {
        super();

        this.popupForm = this.querySelector(".popup-form");
        this.lightbox = this.querySelector(".lightbox")

        this.init();
    }

    init() {
        this.registreCustomEvent();
        this.enableTabIndex();
        this.addListeners();

        // Verifica se o parâmetro contact_posted=true está presente
        const urlParams = new URLSearchParams(window.location.search);
        const shouldOpen = urlParams.get("contact_posted") === "true";

        if (shouldOpen) {
            this.openPopup();
        }
    }

    addListeners() {
        if (this.popupForm) {
            const button = this.querySelector(".btn-close");

            if (button) {
                button.addEventListener("click", (event) =>
                    this.closePopup(event)
                );
            }
        }

        if (this.popupForm) {
            this.lightbox.addEventListener("click", (event) =>
                this.closeByLightbox(event)
            )
        }
    }

    registreCustomEvent() {
        this.addEventListener("openModalForm", (event) => {
            this.openPopup()
        })
    }

    enableTabIndex() {
        this.popupForm.querySelectorAll('button, a, input, [tabindex]').forEach((el) => {
            el.setAttribute('tabindex', '-1');
        });
    }

    disableTabIndex() {
        this.popupForm.querySelectorAll('button, a, input, [tabindex]').forEach((el) => {
            el.removeAttribute('tabindex');
        });
    }

    openPopup() {
        this.popupForm.setAttribute("aria-hidden", false);
        this.disableTabIndex();
    }

    closePopup() {
        this.popupForm.setAttribute("aria-hidden", true);
        this.enableTabIndex();
    }

    closeByLightbox(event) {
        const { target } = event

        if (target.classList.contains('lightbox')) {
            this.closePopup();
        }
    }

    maskProtocol(event) {
        let value = event.currentTarget.value;
        let result = value.replace(/\D/g, "");

        if (result.length >= 8) {
            result = result.replace(/(\d{8})(\d{1})/, "$1/$2");
        }

        event.currentTarget.value = result;
    }
}

customElements.define("ci-modal-form", ModalForm);

class GridSizeSelector extends HTMLElement {
    connectedCallback() {
        const gridWrapper = document.querySelector('#product-grid');
        const buttons = this.querySelectorAll('.product-grid__size-option');

        if (!gridWrapper || buttons.length === 0) return;

        const clearActive = () => {
            buttons.forEach(btn => btn.classList.remove('active'));
        };

        buttons.forEach(btn => {
            btn.addEventListener('click', () => {
                const option = btn.dataset.gridOption;

                clearActive();
                btn.classList.add('active');

                gridWrapper.classList.remove('grid--3-col-desktop', 'grid--4-col-desktop');

                if (option == '3') {
                    gridWrapper.classList.add('grid--3-col-desktop');
                } else if (option == '4') {
                    gridWrapper.classList.add('grid--4-col-desktop');
                }
            });
        });
    }
}

customElements.define('grid-size-selector', GridSizeSelector);

class ButtonSizeGuide extends HTMLElement {
    constructor() {
        super();

        this.button = this.querySelector('.guide-sizes');
        this.modal = document.querySelector('.size-guide__modal');

        this.init();
    }

    init() {
        if (!this.button || !this.modal) return;

        this.button.addEventListener('click', () => {
            this.modal.classList.add('opened');
        });
    }
}

customElements.define('button-size-guide', ButtonSizeGuide);

class ProductSummary extends HTMLElement {
    constructor() {
        super();
        // Referências aos elementos existentes no template
        this.priceContainer = this.querySelector("[data-price-content]");
        this.productImage = this.querySelectorAll("[data-product-image]");
        this.handleProduct = this.querySelectorAll("[data-product-url]");
        this.buttonAddToCart = this.querySelector("[data-add-cart]");
        this.productTags = this.querySelector("[data-product-tags]");
        // Referência ao componente customizado que gerencia variantes
        this.selector = this.querySelector('product-selector');
        this.listeners();
    }

    listeners() {
        // Recebe evento quando uma variante é selecionada no product-selector
        if (this.selector) {
            this.selector.addEventListener('variant-selected', e => this.handleVariantSelection(e));
        }
        // Botão de adicionar ao carrinho
        this.buttonAddToCart.addEventListener('click', () => this.addToCart());
    }

    handleVariantSelection(event) {
        const { variant } = event.detail;
        // Atualizações visuais e funcionais
        this.toggleImage(variant.image);
        this.togglePrice(variant.available, variant.compare_at_price, variant.price);
        this.toggleButtonAddToCart(variant.available);
        this.updateLinks('variant', variant.id);
    }

    toggleImage(src) {
        if (!src) return;
        // Atualiza todas as imagens do produto
        this.productImage.forEach(img => {
            img.src = src;
            img.srcset = src;
        });
    }

    togglePrice(available, comparePrice, price) {
        if (!this.priceContainer) return;
        this.priceContainer.innerHTML = '';
        if (!available) {
            const span = document.createElement('span');
            span.className = 'available';
            span.textContent = 'Indisponível';
            this.priceContainer.appendChild(span);
            return;
        }
        // Se houver preço promocional
        if (Number(comparePrice) > Number(price)) {
            const oldPrice = document.createElement('s');
            oldPrice.className = 'old-price';
            oldPrice.textContent = this.formatCurrency(comparePrice);
            this.priceContainer.appendChild(oldPrice);
            this.updateDiscountTag(comparePrice, price);
        } else {
            this.removeDiscountTag();
        }
        const current = document.createElement('span');
        current.className = 'price';
        current.textContent = this.formatCurrency(price);
        this.priceContainer.appendChild(current);
    }

    updateDiscountTag(comparePrice, price) {
        this.removeDiscountTag();
        const discountValue = Math.round((comparePrice - price) / comparePrice * 100);
        const discountTag = document.createElement('span');
        discountTag.className = 'tag tag--discount';
        discountTag.textContent = `-${discountValue}%`;
        this.productTags.appendChild(discountTag);
    }

    removeDiscountTag() {
        const tag = this.productTags.querySelector('.tag--discount');
        if (tag) tag.remove();
    }

    toggleButtonAddToCart(isAvailable) {
        this.buttonAddToCart.style.display = isAvailable ? 'flex' : 'none';
    }

    updateLinks(param, value) {
        this.handleProduct.forEach(link => {
            if (!link.href) return;
            const url = new URL(link.href, window.location.origin);
            url.searchParams.set(param, value);
            link.href = url.pathname + url.search;
        });
    }

    formatCurrency(cents) {
        return (cents / 100).toLocaleString('pt-BR', {
            style: 'currency',
            currency: window.Shopify.currency.active || 'BRL'
        });
    }

    addToCart() {
        try {
            this.buttonAddToCart.setAttribute('disabled', 'disabled');
            const variantId = Array.from(this.selector.state.variants)
                .find(v => v.id === this.selector.state.currentVariant.id).id;
            const config = fetchConfig('javascript');
            config.headers['X-Requested-With'] = 'XMLHttpRequest';
            delete config.headers['Content-Type'];

            const formData = new FormData();
            const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
            if (cart) {
                formData.append('sections', cart.getSectionsToRender().map(s => s.id));
                formData.append('sections_url', window.location.pathname);
                cart.setActiveElement(document.activeElement);
            }
            formData.append('utf8', '✓');
            formData.append('form_type', 'product');
            formData.append('product-id', this.getAttribute('data-product-id'));
            formData.append('id', variantId);
            formData.append('quantity', 1);
            config.body = formData;

            fetch(routes.cart_add_url, config)
                .then(r => r.json())
                .then(response => {
                    if (response.status) {
                        publish(PUB_SUB_EVENTS.cartError, {
                            source: 'product-summary',
                            productVariantId: variantId,
                            errors: response.errors || response.description,
                            message: response.message
                        });
                    } else if (!cart) {
                        window.location = window.routes.cart_url;
                    } else {
                        publish(PUB_SUB_EVENTS.cartUpdate, {
                            source: 'product-summary',
                            productVariantId: variantId,
                            cartData: response
                        });
                        cart.renderContents(response);
                    }
                })
                .catch(console.error)
                .finally(() => {
                    this.buttonAddToCart.removeAttribute('disabled');
                    if (cart && cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
                });
        } catch (error) {
            window.location.href = this.handleProduct[0].href;
        }
    }
}

customElements.define('product-summary', ProductSummary);

class ProductSelector extends HTMLElement {
    constructor() {
        super();
        // Define se deve limitar as opções renderizadas
        this.limitOptions = this.getAttribute('limit-options') === 'true';

        // Ordem personalizada para os tamanhos
        this.sizeOrder = ['grande', 'médio', 'a5', 'inteligente'];

        // Estado interno do componente
        this.state = {
            variants: [],
            select: {},
            currentVariant: null,
            optionLabels: []
        };

        // Flag usada para evitar auto-seleção múltipla
        this.autoSelectFlag = false;
    }

    connectedCallback() {
        // Carrega o JSON do produto embutido na tag <script>
        const script = this.querySelector('script[type="application/json"]');
        if (!script) {
            console.error('JSON do produto não encontrado');
            return;
        }

        const product = JSON.parse(script.textContent);

        // Inicializa labels de opções e variantes
        this.state.optionLabels = product.options;
        this.state.variants = product.variants.map(v => ({
            id: v.id,
            options: v.options,
            price: v.price,
            compare_at_price: v.compare_at_price,
            available: v.available,
            image: v.featured_media?.preview_image?.src ?? product.featured_image
        }));

        this.productHandle = product.handle;
        this.buildOptions();
    }

    buildOptions() {
        const container = this.querySelector('#options');
        container.innerHTML = '';
        this.renderLevel(0, this.state.variants, container);
    }

    renderLevel(level, variants, container) {
        const label = this.state.optionLabels[level] || '';

        // Cria o fieldset e legend para o nível atual
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('div');
        legend.className = 'legend';

        fieldset.setAttribute('option-fieldset', label.toLowerCase());
        legend.textContent = level > 0 ? `Selecione a ${label}` : label;
        fieldset.appendChild(legend);

        // Exibe o valor selecionado anterior (se houver)
        if (level > 0) {
            const span = document.createElement('span');
            span.textContent = `${this.state.optionLabels[level - 1]}: ${this.state.select[`option${level - 1}`]}`;
            legend.appendChild(span);
        }

        // Agrupa variantes pelas opções do nível atual
        const grouped = variants.reduce((acc, v) => {
            const key = v.options[level];
            if (!acc[key]) acc[key] = [];
            acc[key].push(v);
            return acc;
        }, {});

        let groups = Object.entries(grouped).map(([label, vs]) => ({ label, variants: vs }));

        // Ordena se for o primeiro nível (ex: tamanhos)
        if (level === 0) {
            groups.sort((a, b) => {
                const ai = this.sizeOrder.indexOf(a.label.toLowerCase());
                const bi = this.sizeOrder.indexOf(b.label.toLowerCase());
                if (ai !== -1 || bi !== -1) {
                    if (ai === -1) return 1;
                    if (bi === -1) return -1;
                    return ai - bi;
                }
                return a.label.localeCompare(b.label);
            });
        }

        // Cria os botões de opção
        groups.forEach((group, idx) => {
            if (this.limitOptions && idx >= 3) return;
            const btn = document.createElement('button');
            btn.type = 'button';

            const v = group.variants[0];
            const priceBRL = (v.price / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

            btn.textContent = group.label;

            // Adiciona informações de preço e desconto se for segundo nível ou mais
            if (level > 0) {
                const priceContainer = document.createElement('span');
                priceContainer.className = 'price-container';

                if (v.compare_at_price && v.compare_at_price > v.price) {
                    const compareBRL = (v.compare_at_price / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
                    const discount = Math.round((v.compare_at_price - v.price) / v.compare_at_price * 100);

                    const spanOld = document.createElement('span');
                    spanOld.className = 'price-compare';
                    spanOld.textContent = compareBRL;
                    priceContainer.appendChild(spanOld);

                    const spanPrice = document.createElement('span');
                    spanPrice.className = 'price';
                    spanPrice.textContent = priceBRL;
                    priceContainer.appendChild(spanPrice);

                    const spanDiscount = document.createElement('span');
                    spanDiscount.className = 'discount';
                    spanDiscount.textContent = `-${discount}%`;
                    priceContainer.appendChild(spanDiscount);
                } else {
                    const spanPrice = document.createElement('span');
                    spanPrice.className = 'price';
                    spanPrice.textContent = priceBRL;
                    priceContainer.appendChild(spanPrice);
                }

                btn.appendChild(priceContainer);
            }

            // Listener de clique da opção
            btn.addEventListener('click', event => this.selectOption(level, group.variants, btn, event));
            fieldset.appendChild(btn);
        });

        // Adiciona link para ver mais opções se ultrapassar limite (apenas se houver mais de 1 opção oculta)
        if (this.limitOptions && groups.length > 4) {
            const more = document.createElement('a');
            more.className = 'more-options';
            more.textContent = `+${groups.length - 3}`;
            more.href = `/products/${this.productHandle}`;
            fieldset.appendChild(more);
        }

        container.appendChild(fieldset);

        // Se há apenas uma opção, auto-seleciona (apenas uma vez)
        if (groups.length === 1 && !this.autoSelectFlag) {
            this.autoSelectFlag = true;
            const onlyBtn = fieldset.querySelector('button');
            if (onlyBtn) {
                onlyBtn.click();
            }
        }
    }

    selectOption(level, variants, btn, event) {
        const fs = btn.closest('fieldset');

        // Remove 'active' e 'data-user-clicked' de todos os botões do mesmo grupo
        fs.querySelectorAll('button').forEach(b => {
            b.classList.remove('active');
            b.removeAttribute('data-user-clicked');
        });

        // Define botão clicado como ativo
        btn.classList.add('active');

        // Identifica se foi clique do usuário ou da máquina
        const isUser = event.isTrusted;
        btn.setAttribute('data-user-clicked', isUser.toString());

        this.state.select[`option${level}`] = btn.textContent;

        // Remove os níveis seguintes
        const container = this.querySelector('#options');
        Array.from(container.querySelectorAll('fieldset')).slice(level + 1).forEach(fs => fs.remove());

        // Se há mais níveis, renderiza o próximo
        if (level < this.state.optionLabels.length - 1) {
            const nextLevel = level + 1;
            this.renderLevel(nextLevel, variants, container);

            // Seleciona automaticamente a primeira opção do próximo nível
            const newFs = container.querySelectorAll('fieldset')[nextLevel];
            if (newFs) {
                const firstBtn = Array.from(newFs.querySelectorAll('button')).find(b => !b.disabled);
                if (firstBtn) {
                    firstBtn.click();
                }
            }
        } else {
            // Se último nível, define a variante final e dispara evento
            this.state.currentVariant = variants.find(v => v.available) || variants[0];

            this.dispatchEvent(new CustomEvent('variant-selected', {
                detail: { variant: this.state.currentVariant },
                bubbles: true,
                composed: true
            }));
        }
    }
}

customElements.define('product-selector', ProductSelector);

class ProductSelectorPDP extends HTMLElement {
    constructor() {
        super();
        this.limitOptions = this.getAttribute('limit-options') === 'true';
        this.sizeOrder = ['grande', 'médio', 'a5', 'inteligente'];
        this.state = { variants: [], select: {}, currentVariant: null, optionLabels: [], preSelect: [] };
        this.autoSelectFlag = false;
        this.form = this.getAttribute('data-form-id') || null;
    }

    connectedCallback() {
        const script = this.querySelector('script[type="application/json"]');
        if (!script) return;
        let product;
        try {
            product = JSON.parse(script.textContent);
        } catch (e) {
            return;
        }
        this.state.optionLabels = Array.isArray(product.options) ? product.options : [];
        this.state.variants = (product.variants || []).map(v => ({
            id: v.id,
            options: Array.isArray(v.options) ? v.options : [v.option1, v.option2, v.option3].filter(Boolean),
            available: !!v.available
        }));
        const params = new URLSearchParams(window.location.search);
        const vid = params.get('variant');
        const matched = product.variants && product.variants.find(v => String(v.id) === vid);
        if (matched) {
            this.state.preSelect = Array.isArray(matched.options) ? matched.options : [matched.option1, matched.option2, matched.option3].filter(Boolean);
        }
        this.productHandle = product.handle || '';
        this.buildOptions();
    }

    buildOptions() {
        const container = this.querySelector('#options');
        if (!container) return;
        container.innerHTML = '';
        this.renderLevel(0, this.state.variants, container);
    }

    renderLevel(level, variants, container) {
        const label = this.state.optionLabels[level] || '';
        const fieldset = document.createElement('fieldset');
        const legend = document.createElement('div');
        legend.className = 'legend';
        fieldset.setAttribute('option-fieldset', label.toLowerCase());
        legend.textContent = label;

        fieldset.appendChild(legend);

        if (level === 0) {
            legend.innerHTML = `${label}<button-size-guide class="guide-sizes"><button type="button" class="guide-sizes"><span>Guia de tamanhos</span></button></button-size-guide>`;
        }

        const grouped = variants.reduce((acc, v) => {
            const opts = v.options;
            const key = opts && opts[level];
            if (key !== undefined && key !== null) {
                (acc[key] = acc[key] || []).push(v);
            }
            return acc;
        }, {});

        let groups = Object.entries(grouped).map(([lbl, vs]) => ({ label: lbl, variants: vs }));
        if (level === 0) {
            groups.sort((a, b) => {
                const ai = this.sizeOrder.indexOf(a.label.toLowerCase());
                const bi = this.sizeOrder.indexOf(b.label.toLowerCase());
                if (ai !== -1 || bi !== -1) {
                    if (ai === -1) return 1;
                    if (bi === -1) return -1;
                    return ai - bi;
                }
                return a.label.localeCompare(b.label);
            });
        }

        groups.forEach((grp, idx) => {
            if (this.limitOptions && idx >= 4) return;
            const btn = document.createElement('button');
            btn.type = 'button';
            btn.textContent = grp.label;
            btn.setAttribute('data-available', grp.variants.some(v => v.available));
            if (this.state.select[`option${level}`] === grp.label) btn.classList.add('active');
            btn.addEventListener('click', e => this.selectOption(level, grp.variants, btn, e));
            fieldset.appendChild(btn);
        });

        if (this.limitOptions && groups.length > 4) {
            const more = document.createElement('a');
            more.className = 'more-options';
            more.textContent = `+${groups.length - 4}`;
            more.href = `/products/${this.productHandle}`;
            fieldset.appendChild(more);
        }

        container.appendChild(fieldset);

        if (groups.length === 1 && !this.autoSelectFlag) {
            this.autoSelectFlag = true;
            fieldset.querySelector('button[data-available]')?.click();
            return;
        }

        if (this.state.preSelect[level]) {
            const autoBtn = Array.from(fieldset.querySelectorAll('button')).find(b => b.textContent === this.state.preSelect[level] && b.getAttribute('data-available') === 'true');
            if (autoBtn) autoBtn.click();
        }
    }

    selectOption(level, variants, btn, event) {
        const key = `option${level}`;
        this.state.select[key] = btn.textContent;
        const fs = btn.closest('fieldset');
        fs.querySelectorAll('button').forEach(b => b.classList.remove('active'));
        btn.classList.add('active');

        const container = this.querySelector('#options');
        Array.from(container.querySelectorAll('fieldset')).slice(level + 1).forEach(f => f.remove());

        if (level < this.state.optionLabels.length - 1) {
            this.renderLevel(level + 1, variants, container);
        } else {
            this.state.currentVariant = variants.find(v => v.available) || variants[0];
            this.updateUrl('variant', this.state.currentVariant.id);
            publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
                data: {
                    event,
                    target: this.form ? document.querySelector(`#${this.form}`)?.closest('.product__info-container') : null,
                    selectedOptionValues: [this.state.currentVariant.id]
                }
            });
            if (this.form) {
                const input = document.querySelector(`#${this.form} input[name="id"]`);
                if (input) input.value = this.state.currentVariant.id;
            }
        }
    }

    updateUrl(key, value) {
        const url = new URL(window.location.href);
        if (url.pathname.includes('/products/')) {
            url.searchParams.set(key, value);
            window.history.replaceState({}, '', url);
        }
    }
}

customElements.define('product-selector-pdp', ProductSelectorPDP);


class customPaymentsMethods extends HTMLElement {
    connectedCallback() {
        if (this._ciPaymentsBound) return;
        this._ciPaymentsBound = true;

        this.paymentsMethods = this.querySelector('.view_payment_methods');
        this.modal = document.querySelector('.modal');
        this.cloneModal = document.querySelector('.modal-content__close');

        this._onOpen = () => this.handleModal();
        this._onClose = () => this.handleModalClose();

        this.paymentsMethods?.addEventListener('click', this._onOpen);
        this.cloneModal?.addEventListener('click', this._onClose);
    }

    handleModal() {
        this.modal?.setAttribute('data-modal', 'show');
    }

    handleModalClose() {
        this.modal?.removeAttribute('data-modal');
    }
}
customElements.define('custom-payment-methods', customPaymentsMethods);